import { useState } from 'react'
import { customerData, CustomerTrackingData } from '../../../../utilities/CustomerData'
import { getTrackingURL, refreshAccessToken } from '../../../../utilities/extensions'
import { apiURL } from '../../../../utilities/URLs'
import { useNavigate } from 'react-router-dom'

export function DashboardTracking() {
  const navigate = useNavigate()

  const [customerTrackingData, setCustomerTrackingData] = useState(customerData.customerTrackingData)
  const [courier, setCourier] = useState(customerData.customerTrackingData.trackingCourier)
  const [trackingNumber, setTrackingNumber] = useState(customerData.customerTrackingData.trackingNumber)
  const [otherCourier, setOtherCourier] = useState('')
  const [sendingTrackingInfo, setSendingTrackingInfo] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleCourierChange = (event) => {
    setCourier(event.target.value)
    if (event.target.value !== 'other') {
      setOtherCourier('')
    }
  }

  const handleTrackingNumberChange = (event) => {
    setTrackingNumber(event.target.value)
  }

  const handleOtherCourierChange = (event) => {
    setOtherCourier(event.target.value)
  }

  const handleAddTrackingNumber = async () => {
    const selectedCourier = courier === 'other' ? otherCourier : courier

    if (!selectedCourier || !trackingNumber) {
      setErrorMessage('Please fill out both the courier and tracking number fields.')
      return
    }

    setSendingTrackingInfo(true)
    setErrorMessage('') // Clear any previous error messages

    const trackingInfo = {
      leadId: customerData.customerLeadData.leadId,
      customerId: customerData.customerLeadData.leadContact.contactId,
      trackingCourier: selectedCourier,
      trackingNumber: trackingNumber,
      trackingURL: getTrackingURL(selectedCourier, trackingNumber),
      isEditing: isEditing
    }

    const form = new FormData()
    form.append('trackingInfo', JSON.stringify(trackingInfo))

    const addTrackingResponse = await fetch(`${apiURL}/api/addTrackingInfo`, {
      method: 'POST',
      body: form,
      headers: {
        'Authorization': customerData.accessToken
      }
    })

    const addTrackingData = await addTrackingResponse.json()
    if (addTrackingData.response.error) {
      if (addTrackingData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleAddTrackingNumber() : navigate('/', { state: { expired: true } })
      }
      console.error(addTrackingData.response.error)
    } else {
      if (!isEditing) {
        customerData.customerTrackingData = new CustomerTrackingData(trackingInfo)
        customerData.customerTrackingData.trackingId = addTrackingData.response.trackingId
      } else {
        customerData.customerTrackingData.trackingCourier = selectedCourier
        customerData.customerTrackingData.trackingNumber = trackingNumber
        customerData.customerTrackingData.trackingURL = getTrackingURL(selectedCourier, trackingNumber)
      }

      setCustomerTrackingData(customerData.customerTrackingData)
      setIsEditing(false)
      setSendingTrackingInfo(false)
    }
  }

  const handleDownloadS3Link = async (linkURL) => {
    const s3UrlResponse = await fetch(`${apiURL}/api/generateS3URL?url=${encodeURIComponent(linkURL)}`, {
      headers: {
        'Authorization': customerData.accessToken
      }
    })
    const s3UrlData = await s3UrlResponse.json()
    if (s3UrlData.response.error) {
      if (s3UrlData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleDownloadS3Link(linkURL) : navigate('/', { state: { expired: true } })
      } else
        console.error(s3UrlData.response.error)
    } else
      return window.open(s3UrlData.response.s3Url, '_blank')
  }

  return (
    customerTrackingData.trackingId.length === 0 ? (
      <div className='lg:w-1/2'>
        <p className='font-bold text-gray-500 mt-10'>Sent Your Packet?</p>
        <p className='text-gray-500'>
          Please enter in the tracking number from your courier. This will help us verify your packets to the IRS and provide you with timely updates.
        </p>
        <div className='mt-4'>
          <div className='mb-4'>
            <label htmlFor='courier' className='block text-gray-700 font-bold mb-2'>Select Courier</label>
            <select
              id='courier'
              value={courier}
              onChange={handleCourierChange}
              className='block appearance-none w-full bg-white border border-gray-400 hover:border-gray-500 px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline'
            >
              <option value='' disabled>Select a courier</option>
              <option value='FedEx'>FedEx</option>
              <option value='UPS'>UPS</option>
              <option value='USPS'>USPS</option>
              <option value='DHL'>DHL</option>
              <option value='other'>Other</option>
            </select>
          </div>
          {courier === 'other' && (
            <div className='mb-4'>
              <label htmlFor='otherCourier' className='block text-gray-700 font-bold mb-2'>Other Courier</label>
              <input
                type='text'
                id='otherCourier'
                value={otherCourier}
                onChange={handleOtherCourierChange}
                className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
              />
            </div>
          )}
          <div className='mb-4'>
            <label htmlFor='trackingNumber' className='block text-gray-700 font-bold mb-2'>Tracking Number</label>
            <input
              type='text'
              id='trackingNumber'
              value={trackingNumber}
              onChange={handleTrackingNumberChange}
              className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
            />
          </div>
          {errorMessage && (
            <p className='text-red-500 text-xs italic'>{errorMessage}</p>
          )}
          <div className='flex items-center justify-between'>
            <button onClick={handleAddTrackingNumber} className='bg-primaryColor hover:bg-primaryDarkColor transition duration-200 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline'>
              Submit
            </button>
          </div>
        </div>
        {sendingTrackingInfo && (
          <div className='flex flex-row items-center justify-center gap-2'>
            <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-8 w-8 mt-4 mb-4'></div>
            <p>{isEditing ? 'Updating Tracking Information...' : 'Adding Tracking Information...'}</p>
          </div>
        )}
      </div>
    ) : (
      <>
        <p className='font-bold text-xl mb-4 mt-4'>Tracking Information:</p>
        {isEditing ? (
          <div className='lg:w-1/2'>
            <div className='mb-4'>
              <label htmlFor='courier' className='block text-gray-700 font-bold mb-2'>Select Courier</label>
              <select
                id='courier'
                value={courier}
                onChange={handleCourierChange}
                className='block appearance-none w-full bg-white border border-gray-400 hover:border-gray-500 px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline'
              >
                <option value='' disabled>Select a courier</option>
                <option value='FedEx'>FedEx</option>
                <option value='UPS'>UPS</option>
                <option value='USPS'>USPS</option>
                <option value='DHL'>DHL</option>
                <option value='other'>Other</option>
              </select>
            </div>
            {courier === 'other' && (
              <div className='mb-4'>
                <label htmlFor='otherCourier' className='block text-gray-700 font-bold mb-2'>Other Courier</label>
                <input
                  type='text'
                  id='otherCourier'
                  value={otherCourier}
                  onChange={handleOtherCourierChange}
                  className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                />
              </div>
            )}
            <div className='mb-4'>
              <label htmlFor='trackingNumber' className='block text-gray-700 font-bold mb-2'>Tracking Number</label>
              <input
                type='text'
                id='trackingNumber'
                value={trackingNumber}
                onChange={handleTrackingNumberChange}
                className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
              />
            </div>
            {errorMessage && (
              <p className='text-red-500 text-xs italic'>{errorMessage}</p>
            )}
            <div className='flex gap-4'>
              <button onClick={handleAddTrackingNumber} className='rounded-md bg-blue-500 text-white h-10 w-44 mb-4 hover:bg-blue-600'>
                Update Tracking Info
              </button>
              <button onClick={() => setIsEditing(false)} className='rounded-md bg-primaryColor hover:bg-primaryDarkColor transition duration-200 text-white h-10 w-24 mb-4'>
                Close
              </button>
            </div>
            {sendingTrackingInfo && (
              <div className='flex flex-row items-center justify-center gap-2'>
                <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-8 w-8 mt-4 mb-4'></div>
                <p>{isEditing ? 'Updating Tracking Information...' : 'Adding Tracking Information...'}</p>
              </div>
            )}
          </div>
        ) : (
          <>
            <p className='font-bold text-gray-500 mb-4'>Tracking Courier: <span className='font-normal'>{customerTrackingData.trackingCourier}</span></p>
            <p onClick={() => customerTrackingData.trackingURL.length > 0 && handleDownloadS3Link(customerTrackingData.trackingURL)} className='font-bold text-gray-500 mb-4'>Tracking Number: <span className={`${customerTrackingData.trackingURL.length === 0 ? 'font-normal' : 'font-normal underline text-blue-800 cursor-pointer'}`}>{customerTrackingData.trackingNumber}</span></p>
            <button onClick={() => setIsEditing(true)} className='rounded-md bg-blue-500 text-white h-10 w-40 mb-4 hover:bg-blue-600'>
              Edit Tracking Info
            </button>
          </>
        )}
      </>
    )
  )
}